import { useContext, useEffect, useRef, useState } from "react";
import { Gallery as GridGallery, Image as GridImage } from "react-grid-gallery";
import Skeleton from '@mui/material/Skeleton';

import { LayoutContext, ILayoutContext } from '../';
import { DataLoader } from "../";
import { getMediaStock, entityMediaUrl } from "../../api";
import { HasMedia } from "../../models";

type Only<T, U> = {
  [P in keyof T]: T[P];
} & {
    [P in keyof U]?: never;
  };

type Either<T, U> = Only<T, U> | Only<U, T>

export type MediaGalleryProps = {
  showTitle?: boolean;
  defaultVersion?: string;
  thumbVersion?: string;
  rowHeight?: number;
  className?: string;
  apiPath?: string;
  disableExpand?: boolean;
} & Either<{ id: number }, { mediaItem: HasMedia }>

const MediaGallery = ({ className, id, mediaItem, showTitle, rowHeight, apiPath, thumbVersion, defaultVersion, disableExpand }: MediaGalleryProps) => {
  const layoutContext = useContext(LayoutContext) as ILayoutContext;

  const defaultApiPath = "mediastock";

  const addImageProcess = (src: string) => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve({
        src: src,
        width: img.width,
        height: img.height
      })
      img.onerror = reject
      img.src = src
    })
  }

  const mediaStock = useRef<HasMedia | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [images, setImages] = useState<GridImage[]>([]);

  useEffect(() => {
    const init = async () => {
      if (mediaItem)
        mediaStock.current = mediaItem!;
      else {
        const response = await getMediaStock(id);

        if (response && response.data) {
          mediaStock.current = (response.data.data as HasMedia);
        }
      }

      const { media } = mediaStock.current!;

      if (media && media.length > 0)
        setIsLoading(true);

      const gridImages: GridImage[] = [];

      if (media && mediaStock.current)
        for (const m of media) {
          const imageUrl = entityMediaUrl(mediaStock.current.id, apiPath ?? defaultApiPath, m.file_name, thumbVersion ? thumbVersion : "thumb");

          const x = await addImageProcess(imageUrl) as GridImage;

          gridImages.push(x);
        }

      setImages(gridImages);
      setIsLoading(false);
    }

    init();
  }, [id, thumbVersion]);

  const onClickGridImage = (i: number, image: GridImage) => {
    if (disableExpand)
      return;

    const m = mediaStock.current?.media![i];

    if (m) {
      const imageUrl = entityMediaUrl(mediaStock.current?.id!, apiPath ?? defaultApiPath, m.file_name, defaultVersion ? defaultVersion : "default");

      layoutContext.dialog.show({
        content: <img src={imageUrl} alt={"AM"} />,
        className: `gallery-dialog ${image.width > image.height ? 'fullscreen-landscape' : 'fullscreen-portrait'}`,
        title: " "
      });
    }
  }

  return <div className={`media-gallery ${className} ${disableExpand ? 'disable-expand' : ''}`}>
    {showTitle &&
      <h3>Gallery</h3>}

    {isLoading &&
      <div className="animated-loader">
        <Skeleton variant="rectangular" width={"100%"} height={300} />
        <DataLoader />
      </div>
    }

    {(images && images.length > 0) &&
      <GridGallery images={images} enableImageSelection={false}
        rowHeight={rowHeight ? rowHeight : 256} onClick={onClickGridImage} />}
  </div>;
}

export default MediaGallery;